import React, {
  forwardRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Spinner } from '@chakra-ui/react';

// import { NestedContent, StudyGroup } from '../app/hooks/api/types';
import useJsonCmsContent from './hooks/useJsonCmsContent';
import ContentNavigation from './components/ContentNavigation';
import PresentationNavigation from './components/PresentationNavigation';
import { useQueryParam } from '../../hooks/useQueryParams';
import { trackAction } from '../../helpers/trackAction';
import { useLocation } from 'react-router-dom';
import updateQueryParams from '../../helpers/updateQueryParams';
import Plugin from './components/Plugin/Plugin';

import './style.css';
import {
  JsonCmsContent as JsonCmsContentDataType,
  PluginType,
} from './types/JsonCmsContent';
import { AuthContext, Role } from '../../context/AuthProvider';
import useNoteTaking from './hooks/useNoteTaking';
import NoteDrawer from './components/NoteDrawer';
import { Content, StudyGroup } from '../../generated/graphqlModels';
import useWindowSize from '../app/hooks/useWindowSize';

const goToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

interface JsonCmsContentProps {
  content: Content;
  isChalkedUp: boolean;
  studyGroup: StudyGroup;
  isChalkupsLoading: boolean;
  isTeacherGuideDisplayed: boolean;
  toggleComplete: (value: boolean) => void;
  goToNextResource: () => void;
  popup: Window | null;
  setPopup: (_: Window | null) => void;
  shouldGoNext: number;
  setShouldGoNext: (v: number) => void;
  presentationEnabled: boolean;
  setPresentationEnabled: (v: boolean) => void;
  showAllSections?: boolean;
  hideNavigation?: boolean;
  uncompletable?: boolean;
  isUsingMaxWidth?: boolean;
}

const recursivelyGetTextPlugins = (
  text: string,
  plugin: JsonCmsContentDataType,
) => {
  if (!plugin) {
    return '';
  }
  if (plugin.plugin === 'TextPlugin') {
    text += plugin.data;
  }

  for (const p of plugin.children) {
    text = recursivelyGetTextPlugins(text, p);
  }

  return text;
};

const JsonCmsContent = forwardRef((props: JsonCmsContentProps, ref: any) => {
  const {
    content,
    isChalkedUp,
    studyGroup,
    isChalkupsLoading,
    isTeacherGuideDisplayed,
    toggleComplete,
    goToNextResource,
    popup,
    setPopup,
    shouldGoNext,
    setShouldGoNext,
    presentationEnabled,
    setPresentationEnabled,
    showAllSections,
    hideNavigation,
    uncompletable,
    isUsingMaxWidth,
  } = props;
  const user = useContext(AuthContext);
  const qSectionNum = parseInt(useQueryParam('current-section') || '1');
  const location = useLocation();
  const [queryParamUsed, setIsQueryParamUsed] = useState(false);
  const [page, setPage] = useState<number>(qSectionNum);
  const [currentAssessmentQuestion, setCurrentAssessmentQuestion] =
    useState<number>(0);
  const {
    content: contentData,
    numPages,
    isLoading,
    isPresentation,
    isAssessment,
    numAssessmentQuestions,
    getPreviousContent,
    getNextContent,
  } = useJsonCmsContent(content.pk, showAllSections ? -1 : page);

  const realPage = Math.min(page, numPages);
  const isBackDisabled = realPage <= 1;
  const isNextDisabled =
    realPage === numPages && (uncompletable || !isChalkedUp);

  const { width, height } = useWindowSize();
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<any>(null);

  useLayoutEffect(() => {
    if (contentRef && contentRef.current) {
      setContentHeight(contentRef.current.clientHeight);
    }
  }, [contentRef.current, contentData, width, height]);

  const next = () => {
    if (isNextDisabled) {
      return;
    }
    goToTop();
    if (page === numPages && isChalkedUp && popup) {
      popup.close();
      setPopup(null);
      return;
    }
    if (page === numPages && isChalkedUp) {
      trackAction({
        action_type: 'next',
        action_object: {
          type: 'content',
          pk: content.pk,
        },
        study_group: { pk: studyGroup.pk },
        data: {
          sectionCounter: page,
        },
      });
      goToNextResource();
      setPage(1);
    } else {
      trackAction({
        action_type: 'next',
        action_object: {
          type: 'content',
          pk: content.pk,
        },
        study_group: { pk: studyGroup.pk },
        data: {
          sectionCounter: page,
          nextSectionCounter: page + 1,
        },
      });
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('current-section', (page + 1).toString());
      updateQueryParams(queryParams);
      setPage((c) => c + 1);
    }
  };

  const back = () => {
    if (isBackDisabled) {
      return;
    }
    trackAction({
      action_type: 'previous',
      action_object: {
        type: 'content',
        pk: content.pk,
      },
      study_group: { pk: studyGroup.pk },
      data: {
        sectionCounter: page,
        nextSectionCounter: page - 1,
      },
    });
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('current-section', (page - 1).toString());
    updateQueryParams(queryParams);
    setPage((c) => c - 1);
    goToTop();
  };

  useEffect(() => {
    if (shouldGoNext !== 0) {
      let prevFactor = 1;
      if (shouldGoNext < 0) {
        prevFactor = -1;
      }
      for (let i = 0; i < shouldGoNext * prevFactor; i++) {
        shouldGoNext > 0 ? next() : back();
      }
      setShouldGoNext(0);
    }
  }, [shouldGoNext, setShouldGoNext]);

  const notes = useMemo(() => {
    if (contentData) {
      let n = '';
      if (contentData.plugin === PluginType.TeacherGuidePlugin) {
        n = recursivelyGetTextPlugins(n, contentData);
      } else if (contentData.plugin === PluginType.ContentSectionPlugin) {
        for (const p of contentData.children) {
          if (p?.plugin === PluginType.TeacherGuidePlugin) {
            n = recursivelyGetTextPlugins(n, p);
          }
        }
      }
      return n;
    } else {
      return 'No notes';
    }
  }, [contentData]);

  useEffect(() => {
    if (!!popup) {
      const data = {
        event: 'stateChange',
        data: { isBackDisabled, isNextDisabled, page, numPages, notes },
      };
      popup.postMessage(JSON.stringify(data));
    }
  }, [isNextDisabled, isBackDisabled, page, numPages, notes, popup]);

  useEffect(() => {
    if (!!popup) {
      setTimeout(() => {
        const data = {
          event: 'stateInit',
          data: { isBackDisabled, isNextDisabled, page, numPages, notes },
        };
        popup.postMessage(JSON.stringify(data));
      }, 300);
      setTimeout(() => {
        const data = {
          event: 'stateInit',
          data: { isBackDisabled, isNextDisabled, page, numPages, notes },
        };
        popup.postMessage(JSON.stringify(data));
      }, 600);
      setTimeout(() => {
        const data = {
          event: 'stateInit',
          data: { isBackDisabled, isNextDisabled, page, numPages, notes },
        };
        popup.postMessage(JSON.stringify(data));
      }, 900);
      setTimeout(() => {
        const data = {
          event: 'stateInit',
          data: { isBackDisabled, isNextDisabled, page, numPages, notes },
        };
        popup.postMessage(JSON.stringify(data));
      }, 5000);
      const el = document.querySelector('div.content');
      if (el) {
        (el as HTMLDivElement).classList.add('no-scroll');
      }
    } else {
      const el = document.querySelector('div.content');
      if (el) {
        (el as HTMLDivElement).classList.remove('no-scroll');
      }
    }
  }, [popup]);

  const {
    // loading: isNoteTakingLoading,
    data: userNotes,
    // error: notesError,
    forceHideNoteTaking,
    showNoteTaking,
    toggleNoteTaking,
    createNote,
    updateNoteData,
    updateNoteComment,
    deleteNote,
    selectedNoteId,
    isUpdateCommentLoading,
    isOpen,
    onClose,
    onComment,
  } = useNoteTaking(content.pk, page, user);

  if (isPresentation !== presentationEnabled) {
    setPresentationEnabled(isPresentation);
  }

  useEffect(() => {
    if (queryParamUsed) {
      setPage(1);
      return;
    }
    setIsQueryParamUsed(true);
    setPage(qSectionNum);
  }, [content.id]);

  const NavigationRow: React.FC<{ secondRow?: boolean }> = ({ secondRow }) => {
    if (hideNavigation) {
      return null;
    }
    return popup ? (
      <PresentationNavigation
        isBackDisabled={isBackDisabled}
        isNextDisabled={isNextDisabled}
        next={next}
        back={back}
      />
    ) : isAssessment ? (
      <ContentNavigation
        isBackDisabled={currentAssessmentQuestion <= 0}
        currentSectionCounter={currentAssessmentQuestion + 1}
        totalSections={numAssessmentQuestions}
        isNextDisabled={currentAssessmentQuestion >= numAssessmentQuestions - 1}
        isChalkedUp={false}
        isChalkupsLoading={false}
        goToTop={goToTop}
        toggleComplete={toggleComplete}
        next={() => {
          setCurrentAssessmentQuestion((v) => v + 1);
        }}
        back={() => {
          setCurrentAssessmentQuestion((v) => v - 1);
        }}
        isUsingMaxWidth={isUsingMaxWidth}
        secondRow={secondRow}
      />
    ) : (
      <ContentNavigation
        isBackDisabled={isBackDisabled}
        currentSectionCounter={realPage}
        totalSections={numPages}
        isNextDisabled={isNextDisabled}
        isChalkedUp={isChalkedUp}
        isChalkupsLoading={isChalkupsLoading}
        goToTop={goToTop}
        toggleComplete={toggleComplete}
        next={next}
        back={back}
        onHoverNext={getNextContent}
        onHoverBack={getPreviousContent}
        uncompletable={uncompletable}
        isUsingMaxWidth={isUsingMaxWidth}
        secondRow={secondRow}
      />
    );
  };

  return (
    <Box
      ref={ref}
      background="#FFF"
      color="#000"
      paddingBottom={20}
      className="json_cms_content"
      mx={isUsingMaxWidth ? 'auto' : undefined}
      maxW={isUsingMaxWidth ? '1100px' : undefined}
      _fullScreen={{ overflow: 'scroll', padding: 4 }}
    >
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/bootstrap@4.5.3/dist/css/bootstrap.min.css"
        integrity="sha384-TX8t27EcRE3e/ihU7zmQxVncDAy5uIKz4rEkgIXeMed4M0jlfIDPvg6uqKI2xXr2"
        crossOrigin="anonymous"
      />
      <NavigationRow />
      {isLoading && <Spinner />}
      {contentData && (
        <Box ref={contentRef} id="content" padding={0}>
          <Plugin
            content={contentData}
            isTeacherGuideDisplayed={
              (user?.roles.includes(Role.staff) ||
                user?.roles.includes(Role.teacher) ||
                false) &&
              isTeacherGuideDisplayed &&
              !popup
            }
            // @ts-ignore
            studyGroup={studyGroup}
            currentAssessmentQuestion={
              isAssessment ? currentAssessmentQuestion : -1
            }
            notes={userNotes}
            showExperimentalFeatures={user?.show_experimental_features || false}
            showNoteTaking={showNoteTaking}
            createNote={createNote}
            updateNoteData={updateNoteData}
            deleteNote={deleteNote}
            onComment={onComment}
          />
        </Box>
      )}
      <NavigationRow secondRow={true} />
      <NoteDrawer
        forceHideNoteTaking={forceHideNoteTaking || false}
        isUpdateCommentLoading={isUpdateCommentLoading}
        notes={userNotes}
        selectedNoteId={selectedNoteId}
        isOpen={isOpen}
        showNoteTaking={showNoteTaking}
        toggleNoteTaking={toggleNoteTaking}
        onClose={onClose}
        updateNoteComment={updateNoteComment}
        updateNoteData={updateNoteData}
      />
    </Box>
  );
});

export default JsonCmsContent;
